import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { coreErrorCode } from '@/shared/api/errors'
import {
  closeTrackerReport,
  commentTrackerReport,
  deleteTrackerReport,
  getTrackerReport,
  listTrackerReports,
  TRACKER_OPERATION_UNCERTAIN,
  TRACKER_REPORT_CHANGED,
  TRACKER_REPORT_READ_ONLY,
  updateTrackerReport,
  type CommentTrackerReportInput,
  type TrackerReportDetail,
  type TrackerReportRevisionInput,
  type TrackerReportScope,
  type UpdateTrackerReportInput,
} from '@/shared/api/trackerReportsClient'
import { CAPABILITIES, hasCapability, isDeveloperSession } from '@/shared/session/capabilities'
import type { Session } from '@/shared/session/types'

/**
 * THE REPORTS WORKSPACE'S READS AND WRITES, with one cache discipline.
 *
 * A write's answer IS the report as the server now holds it — every mutation
 * but delete returns the detail — so the cache takes that answer verbatim and
 * never trusts what the browser sent. A surface that painted its own draft as
 * the saved state after a 200 would be right nearly always, which is the worst
 * kind of wrong: the one case where the server normalised a title or dropped
 * an attachment would show the reporter something the tracker does not hold.
 *
 * The list is invalidated rather than patched, because a row's `updatedAt`,
 * `revision` and `permissions` all move on a write and the list is a complete
 * walk that is cheap to take again.
 */

export const trackerReportKeys = {
  root: ['tracker-reports'] as const,
  list: (scope: TrackerReportScope) => ['tracker-reports', 'list', scope] as const,
  detail: (number: number) => ['tracker-reports', 'detail', number] as const,
}

/**
 * WHO MAY OPEN THE WORKSPACE AT ALL: anyone who may file a report. A tester
 * holds `tracker.issues.submit` in development and in production alike; the
 * developer account reaches it through its report authority. The build flag
 * is deliberately NOT read here — the flag hides developer tooling from a
 * release build, and a tester's own reports are not developer tooling.
 */
export function canOpenTrackerReports(session: Session | undefined): boolean {
  return (
    hasCapability(session, CAPABILITIES.trackerIssuesSubmit) ||
    (isDeveloperSession(session) && hasCapability(session, CAPABILITIES.trackerIssuesReport))
  )
}

/**
 * WHO MAY ASK FOR EVERY REPORT: the same authority that opens the native
 * tracker — the developer account, holding the browse capability, in a build
 * that ships developer tooling. Report authority alone does not widen the
 * scope; the server refuses `scope=all` on the same rule, and this gate only
 * keeps the control off a screen where the answer would be a refusal.
 */
export function canBrowseAllTrackerReports(session: Session | undefined): boolean {
  return (
    __TERYSLING_TRACKER_ENABLED__ &&
    isDeveloperSession(session) &&
    hasCapability(session, CAPABILITIES.trackerIssuesBrowse)
  )
}

export function useTrackerReports(scope: TrackerReportScope) {
  return useQuery({
    queryKey: trackerReportKeys.list(scope),
    queryFn: ({ signal }) => listTrackerReports(scope, signal),
    retry: false,
  })
}

export function useTrackerReport(number: number | null) {
  return useQuery({
    queryKey: trackerReportKeys.detail(number ?? 0),
    queryFn: ({ signal }) => getTrackerReport(number as number, signal),
    enabled: number !== null,
    retry: false,
  })
}

export function useTrackerReportCommands() {
  const client = useQueryClient()
  const settle = async (detail: TrackerReportDetail) => {
    client.setQueryData(trackerReportKeys.detail(detail.number), detail)
    await client.invalidateQueries({ queryKey: ['tracker-reports', 'list'] })
  }
  return {
    update: useMutation({
      mutationFn: (input: UpdateTrackerReportInput) => updateTrackerReport(input),
      onSuccess: settle,
    }),
    close: useMutation({
      mutationFn: (input: TrackerReportRevisionInput) => closeTrackerReport(input),
      onSuccess: settle,
    }),
    comment: useMutation({
      mutationFn: (input: CommentTrackerReportInput) => commentTrackerReport(input),
      onSuccess: settle,
    }),
    remove: useMutation({
      mutationFn: (input: TrackerReportRevisionInput) => deleteTrackerReport(input),
      onSuccess: async (number) => {
        client.removeQueries({ queryKey: trackerReportKeys.detail(number) })
        await client.invalidateQueries({ queryKey: ['tracker-reports', 'list'] })
      },
    }),
  }
}

/**
 * The refusals a fresh read can do something about: the report moved under the
 * writer, or it stopped being writable. Both keep the draft and offer a reload;
 * what differs is the sentence.
 */
export function isReportChanged(error: unknown): boolean {
  return coreErrorCode(error) === TRACKER_REPORT_CHANGED
}

export function isReportReadOnly(error: unknown): boolean {
  return coreErrorCode(error) === TRACKER_REPORT_READ_ONLY
}

/**
 * The server could not say whether the write landed. The draft stays, the same
 * key is sent again on retry, and the honest word is "check" — not "failed".
 */
export function isOperationUncertain(error: unknown): boolean {
  return coreErrorCode(error) === TRACKER_OPERATION_UNCERTAIN
}

export function isReportStale(error: unknown): boolean {
  return isReportChanged(error) || isReportReadOnly(error)
}
